import {
  ArtworkHistory,
  ArtworkHistoryMergedAuction,
  useCreateArtworkHistory,
  useDeleteArtworkHistory,
  useEditArtworkHistory,
  useGetHistoryOfArtwork,
  useGetHistoryOfArtworkMerged,
  useReorderArtworkProvenance,
  useUpdateHistorySequence,
} from "@/api/artwork/artwork-history";
import { BooleanBadge } from "@/components/ui/boolean-badge";
import {
  AddButton,
  CancelButton,
  DeleteButton,
  SaveButton,
  UpdateButton,
} from "@/components/ui/custom-buttons";
import {
  CustomDialogContent,
  CustomDialogFooter,
  CustomDialogTitle,
} from "@/components/ui/custom-dialog";
import { DataTable } from "@/components/ui/datatable";
import { Dialog, DialogClose, DialogHeader } from "@/components/ui/dialog";
import { ColumnDef } from "@tanstack/react-table";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import * as AF from "@/components/form-items/AbstractForm";
import {
  MaskableTableSection,
  MaskableTableSectionBody,
  MaskableTableSectionHeader,
} from "@/components/form-items/MaskableTableSection";
import { Form } from "@/components/ui/form";
import { useAuth } from "@/context/auth/AuthContext";
import { RegisterVisibilityField } from "@/context/FormFieldVisibilityContext";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

const formSchema = z.object({
  oeuvresLocalisationHistoriqueHistoriqueNom: z.string(),
  oeuvresLocalisationHistoriqueHistoriqueVille: z.string(),
  oeuvresLocalisationHistoriqueHistoriqueDates: z.string(),
  oeuvresLocalisationHistoriqueHistoriqueCollectionParticuliere: z.boolean(),
});

type ArtworkHistoryForm = z.infer<typeof formSchema>;

const HISTORY_TABLE_MASK_GROUP = "oeuvresLocalisationHistoriqueTableGroup";

/** Dialog inputs unmount when closed — keep them in the Champs drawer. */
const HISTORY_DIALOG_FIELDS = [
  "oeuvresLocalisationHistoriqueHistoriqueNom",
  "oeuvresLocalisationHistoriqueHistoriqueVille",
  "oeuvresLocalisationHistoriqueHistoriqueDates",
] as const;

type CreateOrEditArtworkHistoryProps = {
  disabled?: boolean;
  onOpenChange?: (open: boolean) => void;
  open: boolean;
  artworkHistory?: ArtworkHistory;
  artworkId: string;
};

export const CreateOrEditArtworkHistory = ({
  disabled = false,
  onOpenChange,
  open,
  artworkHistory,
  artworkId,
}: CreateOrEditArtworkHistoryProps) => {
  const isEdit = artworkHistory !== undefined;

  const updater = useEditArtworkHistory();
  const creator = useCreateArtworkHistory();
  const deleter = useDeleteArtworkHistory();

  const { t } = useTranslation();

  const form = useForm<ArtworkHistoryForm>({
    defaultValues: isEdit
      ? artworkHistory
      : {
          oeuvresLocalisationHistoriqueHistoriqueCollectionParticuliere: false,
          oeuvresLocalisationHistoriqueHistoriqueDates: "",
          oeuvresLocalisationHistoriqueHistoriqueNom: "",
          oeuvresLocalisationHistoriqueHistoriqueVille: "",
        },

    resolver: zodResolver(formSchema),
  });

  useEffect(() => {
    if (artworkHistory) {
      form.reset(artworkHistory);
    }
  }, [artworkHistory, form]);

  function onSubmit(values: ArtworkHistoryForm) {
    if (isEdit) {
      updater.mutate(
        {
          historyId: artworkHistory.id,
          artworkId: artworkHistory.artworkId,
          history: values,
        },
        { onSuccess: () => onOpenChange?.(false) },
      );
      return;
    }
    creator.mutate(
      { history: values, artworkId: artworkId },
      {
        onSuccess: () => {
          form.reset();
          onOpenChange?.(false);
        },
      },
    );
  }
  function onDelete() {
    if (!isEdit) return;
    deleter.mutate(
      { artworkId: artworkHistory.artworkId, historyId: artworkHistory.id },
      {
        onSuccess: () => onOpenChange?.(false),
      },
    );
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <CustomDialogContent>
        <Form {...form}>
          <form
            className="flex flex-col"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <DialogHeader>
              <CustomDialogTitle>{t("addArtworkHistory")}</CustomDialogTitle>
            </DialogHeader>
            <AF.Col className="p-6">
              <AF.Text name="oeuvresLocalisationHistoriqueHistoriqueNom" />
              <AF.Text name="oeuvresLocalisationHistoriqueHistoriqueVille" />
              <AF.Text name="oeuvresLocalisationHistoriqueHistoriqueDates" />
              <AF.Checkbox name="oeuvresLocalisationHistoriqueHistoriqueCollectionParticuliere" />
              {isEdit && (
                <DeleteButton
                  variant={"link"}
                  className="text-red-600 w-fit m-auto"
                  iconProps={{ className: "stroke-red-600" }}
                  onClick={onDelete}
                  disabled={disabled}
                >
                  {t("supprimerOeuvresLocalisation")}
                </DeleteButton>
              )}
            </AF.Col>
            <CustomDialogFooter>
              <DialogClose asChild>
                <CancelButton className="flex-1" />
              </DialogClose>
              {isEdit ? (
                <UpdateButton
                  type="submit"
                  className="flex-1"
                  disabled={disabled}
                />
              ) : (
                <SaveButton
                  type="submit"
                  className="flex-1"
                  disabled={disabled}
                />
              )}
            </CustomDialogFooter>
          </form>
        </Form>
      </CustomDialogContent>
    </Dialog>
  );
};

type HistoryRow =
  | (ArtworkHistory & { kind: "history" })
  | (ArtworkHistoryMergedAuction & { kind: "auction" });

const isHistoryRow = (
  row: HistoryRow,
): row is ArtworkHistory & { kind: "history" } => row.kind === "history";

export const ArtworkHistoryTable = ({
  artworkId,
  provenanceMode = false,
}: {
  artworkId: string;
  provenanceMode?: boolean;
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isReader = user?.role === "reader" ? true : false;

  const { data: historyOnly = [] } = useGetHistoryOfArtwork(
    provenanceMode ? undefined : artworkId,
  );
  const { data: merged } = useGetHistoryOfArtworkMerged(
    artworkId,
    provenanceMode,
  );

  const [rows, setRows] = useState<HistoryRow[]>([]);
  const [openEditForm, setOpenEditForm] = useState(false);
  const [openCreateForm, setOpenCreateForm] = useState(false);

  const historyToUpdate = useRef<ArtworkHistory>();

  const sequenceMutator = useUpdateHistorySequence();
  const provenanceReorderMutator = useReorderArtworkProvenance();
  const titleLabel = t(
    provenanceMode
      ? "artwork.table.provenance.title"
      : "artwork.table.history.title",
  );

  const addButton = (
    <AddButton
      type="button"
      onClick={() => setOpenCreateForm(true)}
      disabled={isReader}
    />
  );

  useEffect(() => {
    if (provenanceMode && merged) {
      setRows([...merged.timeline]);
      return;
    }
    setRows(
      historyOnly.map<HistoryRow>((h) => ({ ...h, kind: "history" as const })),
    );
  }, [historyOnly, merged, provenanceMode]);

  function handleUpdate(artworkHistory: ArtworkHistory) {
    historyToUpdate.current = artworkHistory;
    setOpenEditForm(true);
  }

  const datatableColumns = useMemo(
    (): ColumnDef<HistoryRow>[] => [
      {
        size: 1000,
        header: t("tab_oeuvresLocalisationHistoriqueHistoriqueNom"),
        cell: ({ row }) => {
          const r = row.original;
          if (isHistoryRow(r))
            return r.oeuvresLocalisationHistoriqueHistoriqueNom;
          const heading = [
            r.auction?.ventesDescriptionCommissaires,
            r.auction?.ventesDescriptionLieu,
          ]
            .filter(Boolean)
            .join(", ");
          const parts = [
            heading,
            r.oeuvresDansVentesDescriptionNumeroLot
              ? `Lot n°${r.oeuvresDansVentesDescriptionNumeroLot}`
              : null,
          ].filter(Boolean);
          return parts.length
            ? parts.join(" - ")
            : t("sculpture.history.auction");
        },
      },
      {
        header: t("tab_oeuvresLocalisationHistoriqueHistoriqueVille"),
        cell: ({ row }) => {
          const r = row.original;
          if (isHistoryRow(r))
            return r.oeuvresLocalisationHistoriqueHistoriqueVille;
          return [
            r.auction?.ventesDescriptionVille,
            r.auction?.ventesDescriptionPays,
          ]
            .filter(Boolean)
            .join(", ");
        },
      },
      {
        header: t("tab_oeuvresLocalisationHistoriqueHistoriqueDates"),
        cell: ({ row }) => {
          const r = row.original;
          if (isHistoryRow(r))
            return r.oeuvresLocalisationHistoriqueHistoriqueDates;
          return r.auction?.ventesDescriptionDate ?? "";
        },
      },
      {
        header: t(
          "tab_oeuvresLocalisationHistoriqueHistoriqueCollectionParticuliere",
        ),
        cell: ({ row }) => {
          const r = row.original;
          if (!isHistoryRow(r)) return null;
          return (
            <BooleanBadge
              value={
                r.oeuvresLocalisationHistoriqueHistoriqueCollectionParticuliere
              }
            />
          );
        },
      },
    ],
    [t],
  );

  const handleRowDragged = (newData: HistoryRow[]) => {
    setRows(newData);
    if (provenanceMode) {
      provenanceReorderMutator.mutate({
        artworkId,
        timeline: newData.map((r) => ({ kind: r.kind, id: r.id })),
      });
      return;
    }
    sequenceMutator.mutate({
      newSequence: newData.filter(isHistoryRow).map((d) => d.id),
      artworkId,
    });
  };

  return (
    <>
      {HISTORY_DIALOG_FIELDS.map((name) => (
        <RegisterVisibilityField key={name} name={name} label={t(name)} />
      ))}
      <MaskableTableSection>
        <CreateOrEditArtworkHistory
          disabled={isReader}
          artworkId={artworkId}
          artworkHistory={historyToUpdate.current}
          open={openEditForm}
          onOpenChange={setOpenEditForm}
        />
        <CreateOrEditArtworkHistory
          disabled={isReader}
          open={openCreateForm}
          onOpenChange={setOpenCreateForm}
          artworkId={artworkId}
        />
        <MaskableTableSectionHeader
          groupName={HISTORY_TABLE_MASK_GROUP}
          label={titleLabel}
          trailing={addButton}
        />
        <MaskableTableSectionBody groupName={HISTORY_TABLE_MASK_GROUP}>
          <DataTable
            enableDragRows
            onRowDragged={(d) => handleRowDragged(d.newData)}
            columns={datatableColumns}
            data={rows}
            onRowClick={(e) => {
              if (isHistoryRow(e.original)) handleUpdate(e.original);
            }}
          />
        </MaskableTableSectionBody>
      </MaskableTableSection>
    </>
  );
};
